import React, { useState, useEffect } from 'react';
import { 
  X, Save, RefreshCw, Building, DollarSign, FileText, Check, 
  Upload, Download, Trash2, FileEdit, AlertCircle, CheckCircle2, FileCheck 
} from 'lucide-react';
import api from '../services/api';
import InAppTemplateEditorModal from './InAppTemplateEditorModal';
import './PayslipTemplateEditorModal.css';

const AVAILABLE_PAYSLIP_PLACEHOLDERS = [
  { code: '{fullName}', desc: 'Full Name of employee' },
  { code: '{cin}', desc: 'CIN / National Identity ID' },
  { code: '{matricule}', desc: 'Employee Matricule' },
  { code: '{role}', desc: 'Position / Job Title' },
  { code: '{departmentName}', desc: 'Department Name' },
  { code: '{salary}', desc: 'Base Salary / Rate' },
  { code: '{regularHours}', desc: 'Worked Regular Hours' },
  { code: '{overtimeHours}', desc: 'Overtime Hours' },
  { code: '{grossSalary}', desc: 'Calculated Gross Salary' },
  { code: '{cnssDeduction}', desc: 'CNSS Employee Deduction' },
  { code: '{irppTax}', desc: 'IRPP Tax Deduction' },
  { code: '{netSalary}', desc: 'Final Net Salary' },
  { code: '{month}', desc: 'Month (e.g. 2026-10)' },
  { code: '{year}', desc: 'Year (e.g. 2026)' },
  { code: '{companyName}', desc: 'Company Name' },
  { code: '{cnssEmployerNumber}', desc: 'Employer CNSS #' },
];

const PayslipTemplateEditorModal = ({ isOpen, onClose, onSaveSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showInAppEditorModal, setShowInAppEditorModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'config'

  const [formData, setFormData] = useState({
    companyName: 'PURA SOLUTIONS',
    addressLine1: 'TECHNOPOLE DE SOUSSE',
    addressLine2: 'PEPINIERE SOFTECH BUREAU A16',
    addressLine3: 'ROUTE CEINTURE SAHLOUL, 4054 SOUSSE, TUNISIE',
    activity: 'SERVICES INFORMATIQUES',
    cnssEmployerNumber: 'N/A',
    defaultBank: 'ATTIJARI BANK',
    paymentMethod: 'VIREMENT',
    cnssRate: 0.0918,
    irppTaxRate: 0.15,
    professionalFeesRate: 0.10,
    overtimeMultiplier: 1.25,
    headerColor: '#2563eb',
    footerNote: '',
    templateFilePath: null,
    originalFileName: null,
    htmlContent: null,
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplate();
    }
  }, [isOpen]);

  const loadTemplate = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await api.get('/salary/template');
      if (data) {
        setFormData({
          companyName: data.companyName || 'PURA SOLUTIONS',
          addressLine1: data.addressLine1 || '',
          addressLine2: data.addressLine2 || '',
          addressLine3: data.addressLine3 || '',
          activity: data.activity || '',
          cnssEmployerNumber: data.cnssEmployerNumber || 'N/A',
          defaultBank: data.defaultBank || 'ATTIJARI BANK',
          paymentMethod: data.paymentMethod || 'VIREMENT',
          cnssRate: data.cnssRate ?? 0.0918,
          irppTaxRate: data.irppTaxRate ?? 0.15,
          professionalFeesRate: data.professionalFeesRate ?? 0.10,
          overtimeMultiplier: data.overtimeMultiplier ?? 1.25,
          headerColor: data.headerColor || '#2563eb',
          footerNote: data.footerNote || '',
          templateFilePath: data.templateFilePath || null,
          originalFileName: data.originalFileName || null,
          htmlContent: data.htmlContent || null,
        });
      }
    } catch (err) {
      console.error('Error loading payslip template:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const validateAndSetFile = (file) => {
    if (!file.name.endsWith('.docx')) {
      setMessage({ type: 'error', text: 'Please select a valid Word Document (.docx)' });
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setMessage({ type: '', text: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select a .docx template file first.' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = new FormData();
      data.append('file', selectedFile);

      await api.upload('POST', '/salary/template/file', data);
      setMessage({ type: 'success', text: 'Modèle Word (.docx) de fiche de paie téléchargé avec succès!' });
      setSelectedFile(null);
      loadTemplate();
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Échec de l\'envoi du fichier modèle' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = async () => {
    try {
      const blob = await api.downloadBlob('/salary/template/file/download');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = formData.originalFileName || 'fiche_de_paie_modele.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: 'Aucun fichier modèle Word personnalisé trouvé.' });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!window.confirm('Voulez-vous supprimer le modèle personnalisé et réinitialiser au générateur par défaut ?')) return;
    try {
      await api.delete('/salary/template/file');
      setMessage({ type: 'success', text: 'Modèle personnalisé réinitialisé.' });
      loadTemplate();
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      setMessage({ type: 'error', text: 'Échec de la réinitialisation du modèle.' });
    }
  };

  const handleSubmitConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/salary/template', formData);
      setMessage({ type: 'success', text: 'Modèle de fiche de paie sauvegardé avec succès!' });
      if (onSaveSuccess) onSaveSuccess();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error updating template:', err);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde du modèle.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const hasCustomTemplate = !!(formData.templateFilePath || formData.htmlContent);

  return (
    <div className="payslip-editor-modal-overlay" onClick={onClose}>
      <div className="payslip-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payslip-editor-header">
          <h2>
            <FileText size={20} style={{ color: '#38bdf8' }} />
            Gestion & Modification du Modèle de Fiche de Paie
          </h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="payslip-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'upload'}
            className={`payslip-tab${activeTab === 'upload' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={14} /> Fichier Modèle & Édition In-App
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'config'}
            className={`payslip-tab${activeTab === 'config' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('config')}
          >
            <Building size={14} /> Paramètres Société & Taux CNSS
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <RefreshCw size={24} className="spin" /> Chargement du modèle...
          </div>
        ) : (
          <div className="payslip-editor-body">
            {message.text && (
              <div className={`modal-message ${message.type}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: message.type === 'error' ? '#fef2f2' : '#f0fdf4', color: message.type === 'error' ? '#dc2626' : '#16a34a', border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}` }}>
                {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
                <span>{message.text}</span>
              </div>
            )}

            {activeTab === 'upload' && (
              <div className="payslip-upload-grid">
                {/* Active Template Card */}
                <div style={{ background: 'var(--bg-card, #ffffff)', padding: 18, borderRadius: 10, border: '1px solid var(--border-color, #e2e8f0)' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: 15, color: 'var(--text-primary, #0f172a)' }}>Modèle Actuel Utilisé</h3>

                  {hasCustomTemplate ? (
                    <div style={{ padding: 14, background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <FileCheck size={28} style={{ color: '#2563eb' }} />
                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{formData.originalFileName || 'Modèle HTML Édité en Application'}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {formData.templateFilePath ? 'Fichier Word (.docx)' : 'Format HTML Interne'} | Actif
                          </div>
                        </div>
                      </div>
                      <div className="payslip-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowInAppEditorModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 12 }}>
                          <FileEdit size={14} /> Modifier dans l'App
                        </button>
                        {formData.templateFilePath && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={handleDownloadFile} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 12 }}>
                            <Download size={14} /> Télécharger
                          </button>
                        )}
                        <button type="button" className="btn btn-danger btn-sm" onClick={handleDeleteTemplate} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: 12, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6 }}>
                          <Trash2 size={14} /> Réinitialiser
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16, color: '#64748b', fontSize: 13 }}>
                      <p style={{ margin: '0 0 12px 0' }}>Aucun fichier modèle Word personnalisé chargé. Le générateur automatique standard est utilisé.</p>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowInAppEditorModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 12 }}>
                        <FileEdit size={14} /> Créer / Éditer le Modèle dans l'App
                      </button>
                    </div>
                  )}

                  {/* Upload Dropzone */}
                  <h4 style={{ margin: '16px 0 8px 0', fontSize: 14 }}>Charger / Remplacer par un fichier Word (.docx)</h4>
                  <div
                    className={`dropzone ${isDragging ? 'dragging' : ''}`}
                    style={{ border: '2px dashed #cbd5e1', padding: 20, textAlign: 'center', borderRadius: 8, cursor: 'pointer', background: isDragging ? '#eff6ff' : '#fafafa' }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload size={24} style={{ color: '#2563eb', marginBottom: 6 }} />
                    {selectedFile ? (
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        Glissez et déposez votre modèle Word (.docx) ici ou cliquez pour choisir un fichier
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".docx"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="payslip-file-upload-input"
                    />
                    <label htmlFor="payslip-file-upload-input" style={{ display: 'inline-block', marginTop: 10, padding: '4px 12px', background: '#e2e8f0', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
                      Parcourir les fichiers
                    </label>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary payslip-upload-btn"
                    onClick={handleUploadFile}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? <RefreshCw size={16} className="spin" /> : <Upload size={16} />}
                    Enregistrer et Activer le Modèle .docx
                  </button>
                </div>

                {/* Placeholders Guide */}
                <div style={{ background: 'var(--bg-card, #ffffff)', padding: 18, borderRadius: 10, border: '1px solid var(--border-color, #e2e8f0)' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: 15, color: 'var(--text-primary, #0f172a)' }}>Variables Disponibles pour la Fiche de Paie</h3>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0' }}>
                    Insérez ces balises dynamiques dans votre document Word (.docx) ou éditeur HTML pour remplacer automatiquement les informations de l'employé lors de la génération.
                  </p>
                  <div className="payslip-placeholders">
                    {AVAILABLE_PAYSLIP_PLACEHOLDERS.map((p) => (
                      <div key={p.code} style={{ background: '#f1f5f9', padding: '6px 10px', borderRadius: 6, fontSize: 11 }}>
                        <code style={{ color: '#2563eb', fontWeight: 'bold' }}>{p.code}</code>
                        <div style={{ color: '#64748b', fontSize: 10 }}>{p.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'config' && (
              <form onSubmit={handleSubmitConfig} className="payslip-config-grid">
                {/* Form Controls Left */}
                <div className="payslip-form-section">
                  <div className="section-title">
                    <Building size={14} />
                    Informations de l'Employeur
                  </div>

                  <div className="form-group">
                    <label>Nom de l'Entreprise</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Adresse (Ligne 1)</label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Adresse (Ligne 2)</label>
                      <input
                        type="text"
                        name="addressLine2"
                        value={formData.addressLine2}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Adresse (Ligne 3)</label>
                      <input
                        type="text"
                        name="addressLine3"
                        value={formData.addressLine3}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Activité</label>
                      <input
                        type="text"
                        name="activity"
                        value={formData.activity}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>N° CNSS Employeur</label>
                      <input
                        type="text"
                        name="cnssEmployerNumber"
                        value={formData.cnssEmployerNumber}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="section-title" style={{ marginTop: 12 }}>
                    <DollarSign size={14} />
                    Paramètres de Paie & Déductions
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Taux CNSS Salarié (ex: 0.0918 = 9.18%)</label>
                      <input
                        type="number"
                        step="0.0001"
                        name="cnssRate"
                        value={formData.cnssRate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Taux Impôt IRPP (ex: 0.15 = 15%)</label>
                      <input
                        type="number"
                        step="0.0001"
                        name="irppTaxRate"
                        value={formData.irppTaxRate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Banque par Défaut</label>
                      <input
                        type="text"
                        name="defaultBank"
                        value={formData.defaultBank}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Mode de Règlement</label>
                      <input
                        type="text"
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Couleur Principale de l'En-tête</label>
                    <div className="color-picker-row">
                      <input
                        type="color"
                        name="headerColor"
                        value={formData.headerColor}
                        onChange={handleChange}
                      />
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{formData.headerColor}</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Remarque de Bas de Page (Legal Disclaimer)</label>
                    <textarea
                      rows={2}
                      name="footerNote"
                      value={formData.footerNote}
                      onChange={handleChange}
                      placeholder="Notice légale ou remarque complémentaire..."
                    />
                  </div>

                  <div className="payslip-config-actions">
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
                      Enregistrer les Paramètres
                    </button>
                  </div>
                </div>

                {/* Live Preview Right */}
                <div className="payslip-form-section payslip-config-preview">
                  <div className="section-title">Aperçu en Direct</div>
                  <div className="payslip-preview-container">
                    <div className="preview-header-bar" style={{ borderColor: formData.headerColor }}>
                      <div>
                        <div className="preview-company-title" style={{ color: formData.headerColor }}>
                          {formData.companyName || 'NOM ENTREPRISE'}
                        </div>
                        <div className="preview-address-text">{formData.addressLine1}</div>
                        <div className="preview-address-text">N-CNSS: {formData.cnssEmployerNumber}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, fontSize: 11 }}>
                      <strong>Bank:</strong> {formData.defaultBank} ({formData.paymentMethod})
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {showInAppEditorModal && (
        <InAppTemplateEditorModal
          isOpen={showInAppEditorModal}
          onClose={() => setShowInAppEditorModal(false)}
          templateType="payslip"
          templateName="Bulletin de Paie / Fiche de Paie"
          initialHtml={formData.htmlContent || ''}
          onSaveSuccess={() => {
            loadTemplate();
            if (onSaveSuccess) onSaveSuccess();
          }}
        />
      )}
    </div>
  );
};

export default PayslipTemplateEditorModal;
